// React Query hooks for /zones/*. Built on top of the typed wrappers in
// shared/src/api/zones.ts. Mirrors useExhibits.ts (PR 10 ST 7 / PR 11 ST 10).
//
// staffZoneKeys vs zoneKeys: the staff edit-view (ZoneResponse) and the visitor
// view (ZonePublicResponse) come from different endpoints with different
// payload shapes, so they're cached under distinct keys. Both invalidate the
// shared `list` key (since list rows reflect both views).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AssetKind,
  SetNarrationRequest,
  Zone,
  ZoneCodeRegenerationResponse,
  ZoneCreate,
  ZoneDeleteResponse,
  ZoneImageUploadResponse,
  ZoneListItem,
  ZonePublic,
  ZoneQrGenerationResponse,
  ZoneTransitionRequest,
  ZoneUpdate,
  createZone,
  deleteZone,
  generateZoneNarration,
  generateZoneQR,
  getStaffZone,
  getZone,
  getZoneByCode,
  listZones,
  regenerateZoneCode,
  removeZoneMedia,
  setZoneNarration,
  transitionZone,
  updateZone,
  updateZoneMedia,
  uploadZoneImage,
  uploadZoneMedia,
  uploadZoneNarration,
} from "../zones";
import type { ListStatus } from "../listStatus";

export const zoneKeys = {
  all: ["zones"] as const,
  list: () => [...zoneKeys.all, "list"] as const,
  staffDetail: (id: string) => [...zoneKeys.all, "staff-detail", id] as const,
  detail: (id: string) => [...zoneKeys.all, "detail", id] as const,
  byCode: (code: string) => [...zoneKeys.all, "by-code", code] as const,
};

export function useZoneList(status?: ListStatus) {
  return useQuery<ZoneListItem[]>({
    queryKey: [...zoneKeys.list(), { status: status ?? null }],
    queryFn: () => listZones(status),
  });
}

export function useStaffZone(zoneId: string | undefined) {
  return useQuery<Zone>({
    queryKey: zoneKeys.staffDetail(zoneId ?? ""),
    queryFn: () => getStaffZone(zoneId as string),
    enabled: Boolean(zoneId),
  });
}

export function useZone(zoneId: string | undefined) {
  return useQuery<ZonePublic>({
    queryKey: zoneKeys.detail(zoneId ?? ""),
    queryFn: () => getZone(zoneId as string),
    enabled: Boolean(zoneId),
  });
}

export function useZoneByCode(code: string | undefined) {
  return useQuery<ZonePublic>({
    queryKey: zoneKeys.byCode(code ?? ""),
    queryFn: () => getZoneByCode(code as string),
    enabled: Boolean(code),
  });
}

export function useCreateZone() {
  const qc = useQueryClient();
  return useMutation<Zone, Error, ZoneCreate>({
    mutationFn: createZone,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: zoneKeys.list() });
      qc.setQueryData(zoneKeys.staffDetail(created.id), created);
    },
  });
}

export function useUpdateZone() {
  const qc = useQueryClient();
  return useMutation<Zone, Error, { zoneId: string; payload: ZoneUpdate }>({
    mutationFn: ({ zoneId, payload }) => updateZone(zoneId, payload),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: zoneKeys.list() });
      qc.setQueryData(zoneKeys.staffDetail(updated.id), updated);
      qc.invalidateQueries({ queryKey: zoneKeys.detail(updated.id) });
    },
  });
}

export function useTransitionZone() {
  const qc = useQueryClient();
  return useMutation<
    Zone,
    Error,
    { zoneId: string; payload: ZoneTransitionRequest }
  >({
    mutationFn: ({ zoneId, payload }) => transitionZone(zoneId, payload),
    onSuccess: (transitioned) => {
      qc.invalidateQueries({ queryKey: zoneKeys.list() });
      qc.setQueryData(zoneKeys.staffDetail(transitioned.id), transitioned);
      qc.invalidateQueries({ queryKey: zoneKeys.detail(transitioned.id) });
    },
  });
}

export function useDeleteZone() {
  const qc = useQueryClient();
  return useMutation<ZoneDeleteResponse, Error, string>({
    mutationFn: deleteZone,
    onSuccess: (_, zoneId) => {
      qc.invalidateQueries({ queryKey: zoneKeys.list() });
      qc.removeQueries({ queryKey: zoneKeys.staffDetail(zoneId) });
      qc.removeQueries({ queryKey: zoneKeys.detail(zoneId) });
    },
  });
}

export function useUploadZoneImage() {
  const qc = useQueryClient();
  return useMutation<
    ZoneImageUploadResponse,
    Error,
    { zoneId: string; file: File }
  >({
    mutationFn: ({ zoneId, file }) => uploadZoneImage(zoneId, file),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: zoneKeys.list() });
      qc.invalidateQueries({
        queryKey: zoneKeys.staffDetail(variables.zoneId),
      });
      qc.invalidateQueries({
        queryKey: zoneKeys.detail(variables.zoneId),
      });
    },
  });
}

// ── F-F media + narration ───────────────────────────────────────────────
// Mirror the exhibit hooks. Each returns the updated staff Zone, so prime
// the staffDetail cache and invalidate list + visitor detail (mirrors
// useUpdateZone).

function primeZone(qc: ReturnType<typeof useQueryClient>, updated: Zone): void {
  qc.setQueryData(zoneKeys.staffDetail(updated.id), updated);
  qc.invalidateQueries({ queryKey: zoneKeys.list() });
  qc.invalidateQueries({ queryKey: zoneKeys.detail(updated.id) });
}

export function useSetZoneNarration() {
  const qc = useQueryClient();
  return useMutation<
    Zone,
    Error,
    { zoneId: string; language: string; payload: SetNarrationRequest }
  >({
    mutationFn: ({ zoneId, language, payload }) =>
      setZoneNarration(zoneId, language, payload),
    onSuccess: (updated) => primeZone(qc, updated),
  });
}

export function useGenerateZoneNarration() {
  const qc = useQueryClient();
  return useMutation<Zone, Error, { zoneId: string; language: string }>({
    mutationFn: ({ zoneId, language }) =>
      generateZoneNarration(zoneId, language),
    onSuccess: (updated) => primeZone(qc, updated),
  });
}

export function useUploadZoneNarration() {
  const qc = useQueryClient();
  return useMutation<
    Zone,
    Error,
    { zoneId: string; language: string; file: File }
  >({
    mutationFn: ({ zoneId, language, file }) =>
      uploadZoneNarration(zoneId, language, file),
    onSuccess: (updated) => primeZone(qc, updated),
  });
}

export function useUploadZoneMedia() {
  const qc = useQueryClient();
  return useMutation<
    Zone,
    Error,
    {
      zoneId: string;
      file: File;
      kind: AssetKind;
      language?: string | null;
      caption?: string | null;
    }
  >({
    mutationFn: ({ zoneId, ...params }) => uploadZoneMedia(zoneId, params),
    onSuccess: (updated) => primeZone(qc, updated),
  });
}

export function useUpdateZoneMedia() {
  const qc = useQueryClient();
  return useMutation<
    Zone,
    Error,
    {
      zoneId: string;
      assetId: string;
      caption: string | null;
      language: string | null;
    }
  >({
    mutationFn: ({ zoneId, assetId, caption, language }) =>
      updateZoneMedia(zoneId, assetId, { caption, language }),
    onSuccess: (updated) => primeZone(qc, updated),
  });
}

export function useRemoveZoneMedia() {
  const qc = useQueryClient();
  return useMutation<Zone, Error, { zoneId: string; assetId: string }>({
    mutationFn: ({ zoneId, assetId }) => removeZoneMedia(zoneId, assetId),
    onSuccess: (updated) => primeZone(qc, updated),
  });
}

export function useGenerateZoneQR() {
  const qc = useQueryClient();
  return useMutation<ZoneQrGenerationResponse, Error, string>({
    mutationFn: generateZoneQR,
    onSuccess: (_, zoneId) => {
      qc.invalidateQueries({ queryKey: zoneKeys.list() });
      qc.invalidateQueries({ queryKey: zoneKeys.staffDetail(zoneId) });
      qc.invalidateQueries({ queryKey: zoneKeys.detail(zoneId) });
    },
  });
}

export function useRegenerateZoneCode() {
  const qc = useQueryClient();
  return useMutation<ZoneCodeRegenerationResponse, Error, string>({
    mutationFn: regenerateZoneCode,
    onSuccess: (_, zoneId) => {
      qc.invalidateQueries({ queryKey: zoneKeys.list() });
      qc.invalidateQueries({ queryKey: zoneKeys.staffDetail(zoneId) });
      qc.invalidateQueries({ queryKey: zoneKeys.detail(zoneId) });
    },
  });
}
